import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import MySignatureButton from "./signatures/MySignatureButton";
import { api } from "../lib/api";
import { permVisible, isChauffeurOnly, canChauffeurSubmit } from "../lib/permissions";
import OverdueTasksChip from "./OverdueTasksChip";
import BusinessSwitcher from "./BusinessSwitcher";
import NotificationsBell from "./NotificationsBell";
import GlobalSearchBar from "./GlobalSearchBar";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import {
  House, Calendar, Users, Car, IdentificationBadge, MapTrifold,
  UserPlus, UploadSimple, Gear, SignOut, CalendarBlank, Plus, Tray, ArrowUUpLeft, List, Receipt, ChartLineUp, Ticket, CalendarCheck, CheckSquare, ShieldCheck, Warning, Trophy, Target,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, end: true, roles: ["admin", "staff"], perm: "dashboard", group: "" },
  { to: "/new-booking", label: "New Booking", icon: Plus, roles: ["booking_agent", "redline"], group: "Bookings" },
  { to: "/new-booking", label: "New Booking", icon: Plus, roles: ["admin", "staff"], perm: "rental", group: "Bookings" },
  { to: "/my-bookings", label: "My Bookings", icon: Calendar, roles: ["booking_agent", "redline"], group: "Bookings" },
  { to: "/rejected", label: "Rejected Bookings", icon: ArrowUUpLeft, roles: ["booking_agent", "redline"], group: "Bookings" },
  { to: "/availability", label: "Availability", icon: Car, roles: ["affiliate"], group: "Fleet & Calendar" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, roles: ["affiliate"], group: "Fleet & Calendar" },
  { to: "/investor/bookings", label: "Bookings", icon: Car, roles: ["investor"], group: "Bookings" },
  { to: "/bookings/submitted", label: "Submitted Bookings", icon: Tray, roles: ["admin", "staff"], perm: "submitted", group: "Bookings", badge: "submitted" },
  { to: "/bookings/rental", label: "Rental Bookings", icon: Car, roles: ["admin", "staff"], perm: "rental", group: "Bookings" },
  { to: "/bookings/chauffeur", label: "Chauffeur Bookings", icon: IdentificationBadge, roles: ["admin", "staff"], perm: "chauffeur", group: "Bookings" },
  { to: "/bookings/tours", label: "Tours Bookings", icon: MapTrifold, roles: ["admin", "staff"], perm: "tours", group: "Bookings" },
  { to: "/insurance", label: "Insurance Replacement", icon: ShieldCheck, roles: ["admin", "staff"], perm: "insurance", group: "Bookings", badge: "insurance" },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank, roles: ["admin", "staff", "booking_agent", "redline"], perm: "calendar", group: "Fleet & Calendar" },
  { to: "/fleet", label: "Fleet", icon: Car, roles: ["admin", "staff"], perm: "fleet", group: "Fleet & Calendar" },
  { to: "/customers", label: "Clients", icon: Users, roles: ["admin", "staff"], perm: "clients", group: "Clients & Leads" },
  { to: "/leads", label: "Leads", icon: UserPlus, roles: ["admin", "staff", "redline"], perm: "leads", group: "Clients & Leads", badge: "leads" },
  { to: "/invoices", label: "Invoices", icon: Receipt, roles: ["admin"], perm: "invoices", group: "Finance", badge: "invoices" },
  { to: "/financials", label: "Financials", icon: ChartLineUp, roles: ["admin"], perm: "financials", group: "Finance" },
  { to: "/tickets", label: "Tickets & 407", icon: Ticket, roles: ["admin"], perm: "tickets", group: "Finance" },
  { to: "/sales-performance", label: "Sales Performance", icon: Target, roles: ["admin", "staff"], perm: ["sales_performance", "sales_performance_team"], group: "Clients & Leads" },
  { to: "/affiliate-management", label: "Affiliate Management", icon: Trophy, roles: ["admin", "staff"], perm: "affiliates", group: "Operations" },
  { to: "/my-schedule", label: "My Schedule", icon: CalendarCheck, roles: [], perm: "my_schedule", group: "Operations" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, roles: ["admin"], perm: "tasks", group: "Operations", badge: "tasks" },
  { to: "/import", label: "Import", icon: UploadSimple, roles: ["admin", "staff"], perm: "import", group: "Operations" },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["admin"], group: "Admin" },
  { to: "/settings", label: "Account", icon: Gear, roles: ["staff"], perm: "settings", group: "Account" },
  { to: "/settings", label: "Account", icon: Gear, roles: ["booking_agent", "redline", "affiliate"], group: "Account" },
];

const GROUP_ORDER = ["", "Bookings", "Fleet & Calendar", "Clients & Leads", "Finance", "Operations", "Admin", "Account"];

// Detail routes that aren't nav entries still deserve a real title.
const DETAIL_TITLES = [
  { re: /^\/availability$/, label: "Car Availability", group: "Fleet & Calendar" },
  { re: /^\/leaderboard$/, label: "Leaderboard & Challenges", group: "Fleet & Calendar" },
  { re: /^\/affiliate-management$/, label: "Affiliate Management", group: "Operations" },
  { re: /^\/bookings\/view\//, label: "Booking", group: "Bookings" },
  { re: /^\/insurance\/[^/]+$/, label: "Insurance Case", group: "Bookings" },
  { re: /^\/customers\/[^/]+$/, label: "Client Profile", group: "Clients & Leads" },
  { re: /^\/invoices\/[^/]+$/, label: "Invoice", group: "Finance" },
];

const ROLE_LABEL = {
  admin: "Core Team", staff: "B Squad", booking_agent: "Booking Agent",
  affiliate: "Affiliate", redline: "Redline", investor: "Investor",
};

// SALES-015: these two live at MODULE scope on purpose. Defined inside
// Layout's body they were a new component type on every render, and Layout
// re-renders on every navigation (useLocation), so React unmounted and
// remounted the whole sidebar — a fresh <nav> node with scrollTop 0. Hoisted,
// the DOM node survives and the scroll position with it.
const NavItem = ({ item, alerts, onNavigate }) => {
  const badge = item.badge ? alerts[item.badge] : null;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={`nav-${item.label.toLowerCase()}`}
      title={badge ? badge.label : undefined}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-colors duration-150 ${
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <item.icon size={17} weight={isActive ? "fill" : "regular"} />
          <span className="truncate flex-1">{item.label}</span>
          {badge?.count > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-semibold tnum"
              data-testid={`nav-badge-${item.badge}`}
            >
              {badge.count > 99 ? "99+" : badge.count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};

const SidebarBody = ({ groups, alerts, user, onLogout, onNavigate }) => (
  <>
    <div className="px-5 py-6 border-b border-border">
      <img src="/01-auto-group-logo.png" alt="01 Auto Group" className="w-full max-w-[160px] block" style={{ mixBlendMode: "lighten" }} data-testid="sidebar-logo" />
      <div className="label-mono mt-2.5">Fleet &amp; Booking CRM</div>
    </div>
    <nav className="flex-1 px-3 py-4 overflow-y-auto" data-testid="sidebar-nav">
      {groups.map((g) => (
        <div key={g.label || "root"} className="mb-2">
          {g.label && <div className="label-mono px-3 pt-3 pb-2">{g.label}</div>}
          <div className="space-y-0.5">
            {g.items.map((item) => <NavItem key={item.to + item.label} item={item} alerts={alerts} onNavigate={onNavigate} />)}
          </div>
        </div>
      ))}
    </nav>
    <div className="px-3 py-4 border-t border-border">
      <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
        <span className="w-9 h-9 rounded-full bg-primary/15 border border-primary/25 text-primary flex items-center justify-center font-heading text-sm shrink-0">
          {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm truncate" data-testid="current-user-name">{user?.name || user?.email}</div>
          <div className="label-mono mt-0.5">{ROLE_LABEL[user?.role] || "Staff"}</div>
        </div>
        <MySignatureButton />
        <button
          data-testid="logout-button"
          onClick={onLogout}
          title="Sign out"
          aria-label="Sign out"
          className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/[0.06] transition-colors shrink-0"
        >
          <SignOut size={16} />
        </button>
      </div>
    </div>
  </>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navAllowed = (n) => {
    // Investor accounts: only the Bookings tab. Nothing else, ever (no Account/password change).
    if (user?.role === "investor") {
      return n.to === "/investor/bookings";
    }
    // Chauffeur-only accounts get a minimal nav: Calendar, Availability, Settings.
    // With the `chauffeur_submit` add-on they ALSO get New Booking, My Bookings,
    // and Rejected Bookings (submit flow only — no Confirm/Complete).
    if (isChauffeurOnly(user)) {
      const base = n.to === "/calendar" || n.to === "/availability";
      if (base) return true;
      // They get the read-only Account card, not the admin Settings entry.
      if (n.to === "/settings") return n.group === "Account";
      if (canChauffeurSubmit(user)) {
        return n.to === "/new-booking" || n.to === "/my-bookings" || n.to === "/rejected";
      }
      return false;
    }
    if (n.perm) {
      const v = permVisible(user, n.perm);
      if (v !== null) return v;            // admin or permission-managed user
      return n.roles ? n.roles.includes(user?.role) : true;  // legacy fallback
    }
    return !n.roles || n.roles.includes(user?.role);
  };
  // A permission can overlap a role default (e.g. New Booking, Account) — keep
  // the first match per destination so nothing renders twice.
  const nav = NAV.filter(navAllowed).filter(
    (n, i, all) => all.findIndex((x) => x.to === n.to) === i,
  );
  const current =
    nav.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))
    || DETAIL_TITLES.find((d) => d.re.test(location.pathname))
    || nav[0]
    || { label: "" };

  const groups = GROUP_ORDER
    .map((g) => ({ label: g, items: nav.filter((n) => (n.group || "") === g) }))
    .filter((g) => g.items.length > 0);

  // One feed drives every sidebar badge + the topbar alert summary.
  const { data: feed } = useQuery({
    queryKey: ["notifications-feed"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });
  const alerts = Object.fromEntries((feed?.items || []).map((i) => [i.key, i]));
  const topAlert = alerts.submitted || alerts.insurance || alerts.leads || null;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground grain-overlay">
      {/* Desktop sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-card/40 hidden md:flex flex-col sticky top-0 h-screen overflow-y-auto" data-testid="sidebar">
        <SidebarBody groups={groups} alerts={alerts} user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-card flex flex-col md:hidden" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarBody groups={groups} alerts={alerts} user={user} onLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:sticky top-0 z-30 bg-background/70 backdrop-blur-xl border-b border-border" data-testid="topbar">
          {/* Row 1: global elements */}
          <div className="px-4 sm:px-8 pt-3 flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden shrink-0 -ml-1" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu">
              <List size={22} />
            </Button>
            <BusinessSwitcher />
            <div className="flex-1 hidden lg:flex justify-center px-4 min-w-[12rem]"><GlobalSearchBar /></div>
            <div className="flex-1 lg:hidden" />
            {topAlert && (
              <button
                onClick={() => navigate(topAlert.link)}
                data-testid="topbar-alert-summary"
                className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/12 border border-red-500/25 text-red-400 text-xs hover:bg-red-500/20 transition-colors min-w-0"
              >
                <Warning size={13} weight="fill" className="shrink-0" />
                <span className="tnum font-medium shrink-0">{topAlert.count}</span>
                <span className="hidden xl:inline truncate max-w-[190px]">{topAlert.label.toLowerCase()}</span>
              </button>
            )}
            <OverdueTasksChip />
            <NotificationsBell />
          </div>
          {/* Row 2: page context */}
          <div className="px-4 sm:px-8 py-3 sm:py-4 flex flex-wrap items-end gap-x-4 gap-y-3">
            <div className="flex-1 basis-[22rem] min-w-0">
              <div className="label-mono">{ROLE_LABEL[user?.role] || "Staff"} · {current.group || "Console"}</div>
              <h1 className="font-heading text-2xl sm:text-4xl mt-1" data-testid="page-title">{current.label}</h1>
              <div id="page-subtitle-slot" className="mt-1.5 empty:mt-0" />
            </div>
            <div className="flex items-center gap-3 shrink-0 max-w-full">
              <div id="page-actions-slot" />
              <div className="text-right hidden lg:block">
                <div className="label-mono">Today</div>
                <div className="font-heading text-base mt-1">{new Date().toLocaleDateString("en-CA", { weekday: "long", month: "short", day: "numeric" })}</div>
              </div>
            </div>
          </div>
          {/* Mobile search */}
          <div className="px-4 sm:px-8 pb-3 lg:hidden"><GlobalSearchBar /></div>
        </header>
        <main className="flex-1 px-4 sm:px-8 py-6 sm:py-9 relative z-10" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
